import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import { FaDownload, FaTrash, FaUserShield } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedUser } from '../contexts/AuthenticatedUserInfoContext';
import { privacyApi } from '../api/privacyApi';
import { useUsers } from '../hooks/useUsers';
import { isAdmin } from '../utils/permissions';
import { setCookieConsent } from '../utils/cookieConsent';

const PrivacyControls: React.FC = () => {
  const { user, organization, logout } = useAuth();
  const { authenticatedUser } = useAuthenticatedUser();
  const { ownerId } = useUsers();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.600', 'gray.400');

  const [loading, setLoading] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmValue, setConfirmValue] = useState('');

  const accountModal = useDisclosure();
  const workspaceModal = useDisclosure();

  const isOwner = !!ownerId && user?._id === ownerId;
  const isAdminUser = isAdmin(authenticatedUser);

  const handleExportPersonal = async () => {
    setLoading('export-me');
    try {
      await privacyApi.exportMyData();
      toast({ title: 'Personal data export downloaded', status: 'success', duration: 3000 });
    } catch (err: any) {
      toast({
        title: err.response?.data?.message || 'Export failed',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(null);
    }
  };

  const handleExportWorkspace = async () => {
    setLoading('export-workspace');
    try {
      await privacyApi.exportWorkspaceData();
      toast({ title: 'Workspace export downloaded', status: 'success', duration: 3000 });
    } catch (err: any) {
      toast({
        title: err.response?.data?.message || 'Export failed',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading('delete-account');
    try {
      await privacyApi.deleteMyAccount({
        password,
        confirmEmail: confirmValue,
      });
      toast({ title: 'Account deleted', status: 'success', duration: 3000 });
      accountModal.onClose();
      logout();
    } catch (err: any) {
      toast({
        title: err.response?.data?.message || 'Deletion failed',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteWorkspace = async () => {
    setLoading('delete-workspace');
    try {
      await privacyApi.deleteWorkspace({
        password,
        confirmWorkspaceName: confirmValue,
      });
      toast({ title: 'Workspace deleted', status: 'success', duration: 3000 });
      workspaceModal.onClose();
      logout();
    } catch (err: any) {
      toast({
        title: err.response?.data?.message || 'Deletion failed',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(null);
    }
  };

  const openAccountDelete = () => {
    setPassword('');
    setConfirmValue('');
    accountModal.onOpen();
  };

  const openWorkspaceDelete = () => {
    setPassword('');
    setConfirmValue('');
    workspaceModal.onOpen();
  };

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={{ base: 5, md: 6 }}
      boxShadow="sm"
    >
      <HStack mb={2}>
        <FaUserShield />
        <Heading size="md">Privacy & data</Heading>
      </HStack>
      <Text fontSize="sm" color={muted} mb={4}>
        Export your data or request deletion under our{' '}
        <Text as={RouterLink} to="/privacy" color="blue.500">
          Privacy Policy
        </Text>
        .
      </Text>

      <VStack align="stretch" spacing={4}>
        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Download your data
          </Text>
          <HStack flexWrap="wrap" gap={2}>
            <Button
              size="sm"
              leftIcon={<FaDownload />}
              variant="outline"
              onClick={handleExportPersonal}
              isLoading={loading === 'export-me'}
            >
              Export my data
            </Button>
            {isAdminUser && (
              <Button
                size="sm"
                leftIcon={<FaDownload />}
                variant="outline"
                colorScheme="blue"
                onClick={handleExportWorkspace}
                isLoading={loading === 'export-workspace'}
              >
                Export workspace
              </Button>
            )}
          </HStack>
        </Box>

        <Divider />

        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Cookie preferences
          </Text>
          <HStack flexWrap="wrap" gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setCookieConsent(false);
                toast({ title: 'Essential cookies only', status: 'info', duration: 2500 });
              }}
            >
              Essential only
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setCookieConsent(true);
                toast({ title: 'Analytics cookies enabled', status: 'success', duration: 2500 });
              }}
            >
              Allow analytics
            </Button>
          </HStack>
        </Box>

        <Divider />

        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Delete account
          </Text>
          {isOwner ? (
            <Alert status="warning" borderRadius="md" fontSize="sm">
              <AlertIcon />
              As workspace owner, delete the workspace below to remove all data. You cannot delete only
              your account while you own the workspace.
            </Alert>
          ) : (
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              leftIcon={<FaTrash />}
              onClick={openAccountDelete}
            >
              Delete my account
            </Button>
          )}
        </Box>

        {isOwner && isAdminUser && (
          <>
            <Divider />
            <Box>
              <Text fontWeight="semibold" fontSize="sm" mb={2}>
                Delete workspace
              </Text>
              <Text fontSize="sm" color={muted} mb={3}>
                Permanently deletes {organization?.name}, all users, tasks, projects, clients, and billing
                data. This cannot be undone.
              </Text>
              <Button
                size="sm"
                colorScheme="red"
                leftIcon={<FaTrash />}
                onClick={openWorkspaceDelete}
              >
                Delete entire workspace
              </Button>
            </Box>
          </>
        )}
      </VStack>

      <Modal isOpen={accountModal.isOpen} onClose={accountModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete your account</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                This permanently removes your account. Your workspace data will remain for other members.
              </Alert>
              <FormControl isRequired>
                <FormLabel>Confirm your email</FormLabel>
                <Input
                  value={confirmValue}
                  onChange={(e) => setConfirmValue(e.target.value)}
                  placeholder={user?.email}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={accountModal.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteAccount}
              isLoading={loading === 'delete-account'}
            >
              Delete account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={workspaceModal.isOpen} onClose={workspaceModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete workspace</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                All workspace data will be permanently deleted, including for every team member.
              </Alert>
              <FormControl isRequired>
                <FormLabel>Type workspace name to confirm</FormLabel>
                <Input
                  value={confirmValue}
                  onChange={(e) => setConfirmValue(e.target.value)}
                  placeholder={organization?.name}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={workspaceModal.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteWorkspace}
              isLoading={loading === 'delete-workspace'}
            >
              Delete workspace
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PrivacyControls;
